import { get, put } from "utils/api";
import { errorMessage, message } from "actions/message";

/**
 * Admin-only. Fetches every team (active + inactive) with team lead name,
 * active employee count on the team, and attached companies (each with
 * active state and its active product-line labels).
 *
 * Endpoint: GET /api/admin/teams
 * Response: [{ pkiTeamID, sMGID, sTeamName, bActive, sOutboundPhoneNumber,
 *              teamLeadName, activeEmployeeCount,
 *              companies: [{ pkiCompany, sName, bActive, productLines: [...] }] }]
 *
 * Deliberately returns the payload directly instead of dispatching it into the
 * Redux entities store: the Team Info page is the only consumer, the data is
 * admin-only and short-lived, and there's no cross-component sharing that
 * would benefit from a global cache. Keep as component-local state.
 */
export const fetchTeams = () => async dispatch => {
  try {
    return await get("admin/teams");
  } catch (error) {
    const msg = error?.message || (typeof error === "string" ? error : "unknown error");
    dispatch(errorMessage(`Failed to load teams: ${msg}`));
    return null;
  }
};

/**
 * Admin-only. Toggles a team's bActive flag. Deactivation is guardrailed
 * server-side: rejected (400) when the team still has attached active
 * companies. Reactivation is always allowed.
 *
 * Endpoint: PUT /api/admin/teams/{id}/active
 * Body:     { bActive: boolean }
 * Returns:  { success: true } on success,
 *           { success: false, errorMessage, activeCompanyCount } on guardrail block.
 */
export const setTeamActive = (pkiTeamID, bActive) => async dispatch => {
  try {
    await put(`admin/teams/${pkiTeamID}/active`, { bActive });
    dispatch(message(bActive ? "Team activated." : "Team deactivated."));
    return { success: true };
  } catch (error) {
    // Superagent error: server payload lives on error.response.body.
    const body = error?.response?.body;
    const serverMessage = body?.message || error?.message || "Failed to update team.";
    dispatch(errorMessage(serverMessage));
    return {
      success: false,
      errorMessage: serverMessage,
      activeCompanyCount: body?.activeCompanyCount
    };
  }
};
